/**
 * Declarative local actions only. No eval, no arbitrary fetch.
 */

export const ALLOWED_ACTIONS = new Set([
  'setState',
  'resetState',
  'validateState',
  'navigate',
  'openUrl',
  'setFilter',
]);

export const RESERVED_ACTIONS = new Set(['invokeCapability', 'submitForm']);

function setPath(obj, path, value) {
  const parts = String(path).split('.');
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const p = parts[i];
    if (!cur[p] || typeof cur[p] !== 'object') cur[p] = {};
    cur = cur[p];
  }
  cur[parts[parts.length - 1]] = value;
  return obj;
}

function getPath(obj, path) {
  return String(path).split('.').reduce((acc, p) => (acc == null ? undefined : acc[p]), obj);
}

function resolveFrom(from, event, state) {
  if (from === 'event.value') return event && event.value;
  if (from === 'event.checked') return event && event.checked;
  if (from === 'event.row') return event && event.row;
  if (typeof from === 'string' && from.startsWith('event.')) {
    return getPath(event, from.slice('event.'.length));
  }
  if (typeof from === 'string' && from.startsWith('state.')) {
    return getPath(state, from.slice('state.'.length));
  }
  return undefined;
}

/**
 * @returns {{ state: object, validationErrors: object, navigation: string|null }}
 */
export function runActions(actions, { state, defaults, event, schemas }) {
  let next = { ...state };
  // shallow-deep clone first level objects for form-like state
  Object.keys(next).forEach((k) => {
    if (next[k] && typeof next[k] === 'object' && !Array.isArray(next[k])) {
      next[k] = { ...next[k] };
    }
  });
  const validationErrors = {};
  let navigation = null;

  (actions || []).forEach((action) => {
    if (!action || !action.action) return;
    const kind = action.action;
    if (RESERVED_ACTIONS.has(kind)) {
      validationErrors._actions = `reserved action ${kind} is not enabled`;
      return;
    }
    if (!ALLOWED_ACTIONS.has(kind)) {
      validationErrors._actions = `unknown action ${kind}`;
      return;
    }
    if (kind === 'setState') {
      const value =
        action.value !== undefined ? action.value : resolveFrom(action.from, event, next);
      setPath(next, action.path, value);
    } else if (kind === 'resetState') {
      next = JSON.parse(JSON.stringify(defaults || {}));
    } else if (kind === 'validateState') {
      const schema = action.schema || (schemas && schemas[action.schemaRef]);
      if (schema && schema.required) {
        schema.required.forEach((field) => {
          const val = getPath(next, field);
          if (val == null || val === '') {
            validationErrors[field] = 'Required';
          }
        });
      }
      if (schema && schema.properties) {
        Object.keys(schema.properties).forEach((field) => {
          const prop = schema.properties[field];
          const val = getPath(next, field);
          if (prop && prop.format === 'email' && val) {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(val))) {
              validationErrors[field] = 'Invalid email';
            }
          }
        });
      }
    } else if (kind === 'navigate') {
      navigation = action.to || action.path || null;
    } else if (kind === 'openUrl') {
      const url = action.url || resolveFrom(action.from, event, next);
      if (typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/'))) {
        if (typeof window !== 'undefined') window.open(url, '_blank', 'noopener,noreferrer');
      }
    } else if (kind === 'setFilter') {
      const value =
        action.value !== undefined ? action.value : resolveFrom(action.from, event, next);
      setPath(next, action.path || 'filterText', value);
    }
  });

  return { state: next, validationErrors, navigation };
}

export function initialStateFromDeclarations(stateDecls) {
  const out = {};
  if (!stateDecls || typeof stateDecls !== 'object') return out;
  Object.keys(stateDecls).forEach((k) => {
    const decl = stateDecls[k];
    out[k] = decl && 'default' in decl ? JSON.parse(JSON.stringify(decl.default)) : null;
  });
  return out;
}
